import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Typography, Box, IconButton, CircularProgress, alpha,
} from '@mui/material';
import { Add, Remove, Close as CloseIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { backendClient } from '../../../../api/backend';

const ManageSeatsDialog = ({ open, onClose, currentSeats, activeUsers, theme, onSuccess }) => {
    const { t } = useTranslation();
    const [quantity, setQuantity] = useState(currentSeats);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const apiPricing = useSelector(state => state.pricing?.data);

    useEffect(() => {
        setQuantity(currentSeats);
        setError('');
    }, [currentSeats, open]);

    const proPlan = apiPricing?.plans?.find(p => p.slug === 'pro');
    const volumeTiers = proPlan?.volume_tiers || [];
    const sym = apiPricing?.currency_symbol || '$';
    const pos = apiPricing?.symbol_position || 'prefix';

    const fmt = (amount) => pos === 'prefix' ? `${sym}${amount}` : `${amount} ${sym}`;

    const getPerSeatPrice = (seats) => {
        const tier = volumeTiers.find(t => seats >= t.min && seats <= t.max);
        return tier ? tier.monthly_per_seat : (volumeTiers[0]?.monthly_per_seat || 199);
    };

    const getAnnualPerSeat = (seats) => {
        const tier = volumeTiers.find(t => seats >= t.min && seats <= t.max);
        return tier ? tier.annual_per_seat : (volumeTiers[0]?.annual_per_seat || 2388);
    };

    const totalAnnual = getAnnualPerSeat(quantity) * quantity;
    const canDecrease = quantity > 1 && quantity > activeUsers;

    const handleSubmit = async () => {
        if (quantity === currentSeats) return;
        setLoading(true);
        setError('');
        try {
            await backendClient.post('api/billing/update-seats', { quantity });
            onSuccess?.(quantity);
            onClose();
        } catch (err) {
            setError(err.message || t('error_updating_seats', 'Error updating seats'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: '12px', border: `1px solid ${theme.palette.divider}` } }}>
            <DialogTitle sx={{ fontWeight: 600, pb: 0 }}>
                {t('manage_seats', 'Gérer les accès')}
                <IconButton onClick={onClose} disabled={loading}
                    sx={{ position: 'absolute', right: 8, top: 8, color: theme.palette.text.secondary }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 3, mt: 1 }}>
                    {t('seats_description', 'Ajustez le nombre d\'accès utilisateur pour votre abonnement Pro.')}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 3 }}>
                    <IconButton onClick={() => setQuantity(q => Math.max(1, Math.max(activeUsers, q - 1)))}
                        disabled={!canDecrease}
                        sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: '8px' }}>
                        <Remove />
                    </IconButton>
                    <Typography variant="h3" sx={{ fontWeight: 700, minWidth: 60, textAlign: 'center' }}>
                        {quantity}
                    </Typography>
                    <IconButton onClick={() => setQuantity(q => Math.min(10, q + 1))}
                        disabled={quantity >= 10}
                        sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: '8px' }}>
                        <Add />
                    </IconButton>
                </Box>

                <Typography variant="body2" sx={{ textAlign: 'center', color: theme.palette.text.secondary, mb: 2 }}>
                    {activeUsers} utilisateur{activeUsers !== 1 ? 's' : ''} actif{activeUsers !== 1 ? 's' : ''} sur {quantity}
                </Typography>

                {/* Volume tiers display */}
                {volumeTiers.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        {volumeTiers.map((tier, idx) => (
                            <Box key={idx} sx={{
                                display: 'flex', justifyContent: 'space-between', py: 0.75, px: 2, borderRadius: 1, mb: 0.5,
                                bgcolor: quantity >= tier.min && quantity <= tier.max ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                                border: quantity >= tier.min && quantity <= tier.max ? `1px solid ${alpha(theme.palette.primary.main, 0.2)}` : '1px solid transparent',
                            }}>
                                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                    {tier.min}-{tier.max} utilisateurs
                                </Typography>
                                <Typography variant="body2" sx={{
                                    fontWeight: 600,
                                    color: quantity >= tier.min && quantity <= tier.max ? theme.palette.primary.main : theme.palette.text.secondary,
                                }}>
                                    {fmt(tier.monthly_per_seat)}/util./mois
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}

                <Box sx={{ p: 2, bgcolor: theme.palette.background.default, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Prix par utilisateur</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmt(getPerSeatPrice(quantity))}/mois</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Total annuel ({quantity} {quantity === 1 ? 'accès' : 'accès'})</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>{fmt(new Intl.NumberFormat().format(totalAnnual))}/an</Typography>
                    </Box>
                </Box>

                {quantity !== currentSeats && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 2, color: theme.palette.text.secondary, textAlign: 'center' }}>
                        {quantity > currentSeats
                            ? 'Le montant au prorata sera facturé immédiatement pour les jours restants de la période en cours.'
                            : 'Un crédit au prorata sera appliqué à votre prochaine facture.'
                        }
                    </Typography>
                )}

                {error && (
                    <Typography variant="body2" sx={{ mt: 2, color: theme.palette.error.main, textAlign: 'center' }}>
                        {error}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={loading} color="inherit" sx={{ textTransform: 'none' }}>
                    Annuler
                </Button>
                <Button onClick={handleSubmit} disabled={loading || quantity === currentSeats} variant="contained"
                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}>
                    {loading && <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />}
                    {quantity > currentSeats ? `Ajouter ${quantity - currentSeats} accès` : quantity < currentSeats ? `Retirer ${currentSeats - quantity} accès` : 'Aucun changement'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ManageSeatsDialog;
